import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ETH } from '../economics/ecoShared';

import {
  fitHyperbolicDecline,
} from './declineCalculations';

import type {
  DeclineDataPoint,
} from './declineCalculations';

import {
  buildSegmentedDecline,
  detectDeclineDataIssues,
} from './segmentedDeclineCalculations';

export default function SegmentedDeclinePanel({
  data,
  forecastEnd,
}: {
  data: DeclineDataPoint[];
  forecastEnd: number;
}) {
  const [
    fitStartDate,
    setFitStartDate,
  ] = useState(
    data[0]?.date ?? '',
  );

  const [
    autoExclude,
    setAutoExclude,
  ] = useState(true);

  const [
    terminalDecline,
    setTerminalDecline,
  ] = useState(5);

  useEffect(() => {
    if (
      data.length === 0
    ) {
      setFitStartDate('');
      return;
    }

    const exists =
      data.some(
        (point) =>
          point.date ===
          fitStartDate,
      );

    if (!exists) {
      setFitStartDate(
        data[0].date,
      );
    }
  }, [
    data,
    fitStartDate,
  ]);

  const issues =
    useMemo(
      () =>
        detectDeclineDataIssues(
          data,
        ),
      [data],
    );

  const startIndex =
    Math.max(
      0,
      data.findIndex(
        (point) =>
          point.date ===
          fitStartDate,
      ),
    );

  const excludedIndices =
    useMemo(() => {
      return new Set(
        issues
          .filter(
            (issue) =>
              issue.excludeSuggested,
          )
          .map(
            (issue) =>
              issue.index,
          ),
      );
    }, [issues]);

  const selectedData =
    useMemo(() => {
      return data.filter(
        (
          _point,
          index,
        ) =>
          index >=
            startIndex &&
          (
            !autoExclude ||
            !excludedIndices.has(
              index,
            )
          ),
      );
    }, [
      data,
      startIndex,
      autoExclude,
      excludedIndices,
    ]);

  const fitOriginT =
    data[startIndex]?.t ??
    0;

  const normalizedData =
    useMemo(() => {
      return selectedData.map(
        (point) => ({
          ...point,
          t:
            point.t -
            fitOriginT,
        }),
      );
    }, [
      selectedData,
      fitOriginT,
    ]);

  const hyperbolic =
    useMemo(
      () =>
        fitHyperbolicDecline(
          normalizedData,
        ),
      [normalizedData],
    );

  const localForecastEnd =
    Math.max(
      0,
      forecastEnd -
        fitOriginT,
    );

  const segmented =
    useMemo(() => {
      if (!hyperbolic) {
        return null;
      }

      return buildSegmentedDecline(
        hyperbolic,
        terminalDecline,
        localForecastEnd,
      );
    }, [
      hyperbolic,
      terminalDecline,
      localForecastEnd,
    ]);

  const transitionDate =
    segmented &&
    selectedData.length > 0
      ? addDays(
          selectedData[0].date,
          segmented.transitionT,
        )
      : '--';

  return (
    <div
      style={{
        marginTop: 14,
      }}
    >
      <div
        style={{
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          生产数据预处理与分段递减
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color:
              ETH.textMuted,
          }}
        >
          识别生产资料异常候选点，
          允许指定拟合起点，
          并采用双曲递减→终端指数递减进行长期预测
        </div>
      </div>

      {/* 参数设置 */}
      <div
        style={{
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems:
              'flex-end',
            gap: 22,
            flexWrap: 'wrap',
          }}
        >
          <label
            style={{
              display: 'grid',
              gap: 5,
            }}
          >
            <span
              style={{
                fontSize: 11,
                color:
                  ETH.textSec,
              }}
            >
              拟合起点
            </span>

            <select
              value={
                fitStartDate
              }
              onChange={(
                event,
              ) =>
                setFitStartDate(
                  event.target
                    .value,
                )
              }
              style={
                inputStyle
              }
            >
              {data.map(
                (
                  point,
                  index,
                ) => (
                  <option
                    key={`${point.date}-${index}`}
                    value={
                      point.date
                    }
                  >
                    {point.date}
                  </option>
                ),
              )}
            </select>
          </label>

          <label
            style={{
              display: 'grid',
              gap: 5,
            }}
          >
            <span
              style={{
                fontSize: 11,
                color:
                  ETH.textSec,
              }}
            >
              终端有效年递减率
            </span>

            <div
              style={{
                display: 'flex',
                alignItems:
                  'center',
                gap: 6,
              }}
            >
              <input
                type="number"
                min={0.1}
                max={50}
                step={0.5}
                value={
                  terminalDecline
                }
                onChange={(
                  event,
                ) =>
                  setTerminalDecline(
                    Number(
                      event.target
                        .value,
                    ),
                  )
                }
                style={{
                  ...inputStyle,
                  width: 110,
                }}
              />

              <span
                style={{
                  fontSize: 11,
                  color:
                    ETH.textMuted,
                }}
              >
                %
              </span>
            </div>
          </label>

          <label
            style={{
              display: 'flex',
              alignItems:
                'center',
              gap: 7,
              minHeight: 32,
              fontSize: 11,
              color:
                ETH.textSec,
            }}
          >
            <input
              type="checkbox"
              checked={
                autoExclude
              }
              onChange={(
                event,
              ) =>
                setAutoExclude(
                  event.target
                    .checked,
                )
              }
            />

            自动排除孤立异常候选点
          </label>

          <div
            style={{
              marginLeft:
                'auto',
              fontSize: 11,
              color:
                ETH.textMuted,
            }}
          >
            终端递减率为工程控制参数，
            应结合区块经验进行调整
          </div>
        </div>
      </div>

      {/* 数据质量 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(4, minmax(0, 1fr))',
          gap: 12,
          marginTop: 14,
        }}
      >
        <StatusCard
          title="原始有效点"
          value={`${data.length} 条`}
          description="来自生产动态数据"
        />

        <StatusCard
          title="当前拟合点"
          value={`${selectedData.length} 条`}
          description="拟合起点及自动排除后"
        />

        <StatusCard
          title="质量提示"
          value={`${issues.length} 项`}
          description="仅作为资料复核提示"
        />

        <StatusCard
          title="自动排除候选"
          value={`${
            issues.filter(
              (issue) =>
                issue.excludeSuggested,
            ).length
          } 项`}
          description="主要针对孤立异常点"
        />
      </div>

      {/* 质量问题表 */}
      <div
        style={{
          marginTop: 14,
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          生产数据质量诊断
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color:
              ETH.textMuted,
          }}
        >
          程序只标记“候选问题”，
          最终是否剔除应结合现场生产日报和措施记录判断
        </div>

        {issues.length === 0 ? (
          <div
            style={{
              padding:
                '24px 0',
              textAlign:
                'center',
              color:
                ETH.textMuted,
              fontSize: 11,
            }}
          >
            当前未发现明显的数据质量候选问题
          </div>
        ) : (
          <div
            style={{
              marginTop: 12,
              overflowX:
                'auto',
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse:
                  'collapse',
                fontSize: 11,
              }}
            >
              <thead>
                <tr>
                  <th
                    style={
                      thStyle
                    }
                  >
                    日期
                  </th>

                  <th
                    style={
                      thStyle
                    }
                  >
                    类型
                  </th>

                  <th
                    style={
                      thStyle
                    }
                  >
                    说明
                  </th>

                  <th
                    style={
                      thStyle
                    }
                  >
                    自动处理
                  </th>
                </tr>
              </thead>

              <tbody>
                {issues.map(
                  (
                    issue,
                    index,
                  ) => (
                    <tr
                      key={`${issue.date}-${issue.type}-${index}`}
                    >
                      <td
                        style={
                          tdStyle
                        }
                      >
                        {
                          issue.date
                        }
                      </td>

                      <td
                        style={
                          tdStyle
                        }
                      >
                        {
                          issue.type
                        }
                      </td>

                      <td
                        style={
                          tdStyle
                        }
                      >
                        {
                          issue.message
                        }
                      </td>

                      <td
                        style={{
                          ...tdStyle,
                          textAlign:
                            'center',
                        }}
                      >
                        {issue.excludeSuggested
                          ? '建议排除'
                          : '人工核查'}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 分段模型结果 */}
      <div
        style={{
          marginTop: 14,
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          双曲→指数分段递减
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color:
              ETH.textMuted,
          }}
        >
          前期使用双曲递减，
          当瞬时递减率下降至设定终端递减率后自动切换为指数递减
        </div>

        {!hyperbolic ||
        !segmented ? (
          <div
            style={{
              height: 140,
              display: 'flex',
              alignItems:
                'center',
              justifyContent:
                'center',
              color:
                ETH.textMuted,
              fontSize: 11,
            }}
          >
            当前拟合数据不足或无法完成双曲递减拟合
          </div>
        ) : (
          <>
            <div
              style={{
                marginTop: 14,
                display: 'grid',
                gridTemplateColumns:
                  'repeat(4, minmax(0, 1fr))',
                gap: 12,
              }}
            >
              <StatusCard
                title="双曲指数 b"
                value={fmt(
                  hyperbolic.b,
                  3,
                )}
                description="前期双曲递减参数"
              />

              <StatusCard
                title="初始 Di"
                value={`${fmt(
                  hyperbolic.di,
                  6,
                )} 1/d`}
                description="拟合起点名义递减率"
              />

              <StatusCard title="预计转换时间" value={`${fmt(segmented.transitionT / 365, 2)} 年`} description={`${segmented.transitionT > localForecastEnd ? '转换点超出当前预测期，本次预测范围内全部采用双曲递减段' : '双曲递减达到终端递减率后切换为指数尾段'}${transitionDate === '--' ? '' : `；约 ${transitionDate}`}`} />

              <StatusCard title="转换时日产气" value={segmented.transitionQ < 0.001 ? '<0.001 ×10⁴m³/d' : `${fmt(segmented.transitionQ, 3)} ×10⁴m³/d`} description="由双曲段进入指数段" />
            </div>

            <div
              style={{
                marginTop: 12,
                display: 'grid',
                gridTemplateColumns:
                  'repeat(3, minmax(0, 1fr))',
                gap: 12,
              }}
            >
              <StatusCard
                title="双曲段累计产气"
                value={`${fmt(
                  segmented.cumulativeHyperbolic,
                  2,
                )} ×10⁴m³`}
                description="转换点以前"
              />

              <StatusCard
                title="指数段累计产气"
                value={`${fmt(
                  segmented.cumulativeExponential,
                  2,
                )} ×10⁴m³`}
                description="转换点以后"
              />

              <StatusCard
                title="预测期模型累计产气"
                value={`${fmt(
                  segmented.cumulativeTotal,
                  2,
                )} ×10⁴m³`}
                description="拟合起点至预测终点"
              />
            </div>

            <SegmentedChart
              data={data}
              selectedData={
                selectedData
              }
              fitOriginT={
                fitOriginT
              }
              forecastEnd={
                forecastEnd
              }
              segmented={
                segmented
              }
            />
          </>
        )}
      </div>

      <div
        style={{
          marginTop: 14,
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
          fontSize: 11,
          lineHeight: 1.8,
          color:
            ETH.textMuted,
        }}
      >
        <div>
          · 拟合起点用于排除早期返排、
          清液和生产制度尚未稳定阶段对递减分析的影响。
        </div>

        <div>
          · 长时间数据间隔、
          产量突增等只作为人工核查提示，
          不会被程序自动删除。
        </div>

        <div>
          · 只有孤立异常候选点在开启
          “自动排除”时从拟合数据中暂时剔除。
        </div>

        <div>
          · 分段递减用于抑制长期双曲外推造成的过高累计产量预测。
        </div>
      </div>
    </div>
  );
}

function SegmentedChart({
  data,
  selectedData,
  fitOriginT,
  forecastEnd,
  segmented,
}: {
  data:
    DeclineDataPoint[];

  selectedData:
    DeclineDataPoint[];

  fitOriginT: number;

  forecastEnd: number;

  segmented:
    NonNullable<
      ReturnType<
        typeof buildSegmentedDecline
      >
    >;
}) {
  const width = 900;
  const height = 350;

  const left = 62;
  const right = 30;
  const top = 25;
  const bottom = 48;

  const plotW =
    width -
    left -
    right;

  const plotH =
    height -
    top -
    bottom;

  const maxT =
    Math.max(
      forecastEnd,
      1,
    );

  const maxQ =
    Math.max(
      1,
      ...data.map(
        (point) =>
          point.q,
      ),
      ...segmented.curve.map(
        (point) =>
          point.q,
      ),
    ) * 1.08;

  const x = (
    t: number,
  ) =>
    left +
    (
      t / maxT
    ) *
      plotW;

  const y = (
    q: number,
  ) =>
    top +
    (
      1 -
      q / maxQ
    ) *
      plotH;

  const selectedSet =
    new Set(
      selectedData.map(
        (point) =>
          `${point.date}|${point.t}`,
      ),
    );

  const hyperbolicPath =
    segmented.curve
      .filter(
        (point) =>
          point.phase ===
          'hyperbolic',
      )
      .map(
        (
          point,
          index,
        ) =>
          `${
            index === 0
              ? 'M'
              : 'L'
          } ${x(
            fitOriginT +
              point.t,
          ).toFixed(
            2,
          )} ${y(
            point.q,
          ).toFixed(
            2,
          )}`,
      )
      .join(' ');

  const exponentialPoints =
    segmented.curve.filter(
      (point) =>
        point.phase ===
        'exponential',
    );

  const exponentialPath =
    exponentialPoints
      .map(
        (
          point,
          index,
        ) =>
          `${
            index === 0
              ? 'M'
              : 'L'
          } ${x(
            fitOriginT +
              point.t,
          ).toFixed(
            2,
          )} ${y(
            point.q,
          ).toFixed(
            2,
          )}`,
      )
      .join(' ');

  const transitionAbsoluteT =
    fitOriginT +
    segmented.transitionT;

  return (
    <div
      style={{
        marginTop: 14,
        overflowX:
          'auto',
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{
          width: '100%',
          minWidth: 720,
          height,
          display: 'block',
        }}
      >
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill={ETH.bg}
        />

        {Array.from(
          {
            length: 6,
          },
          (_, index) =>
            maxQ *
            index /
            5,
        ).map(
          (value) => (
            <g key={value}>
              <line
                x1={left}
                x2={
                  width -
                  right
                }
                y1={y(value)}
                y2={y(value)}
                stroke={
                  ETH.headerBorder
                }
              />

              <text
                x={
                  left - 8
                }
                y={
                  y(value) +
                  4
                }
                textAnchor="end"
                fontSize={10}
                fill={
                  ETH.textMuted
                }
              >
                {value.toFixed(
                  1,
                )}
              </text>
            </g>
          ),
        )}

        {Array.from(
          {
            length: 6,
          },
          (_, index) =>
            maxT *
            index /
            5,
        ).map(
          (value) => (
            <g key={value}>
              <line
                x1={x(value)}
                x2={x(value)}
                y1={top}
                y2={
                  height -
                  bottom
                }
                stroke={
                  ETH.headerBorder
                }
              />

              <text
                x={x(value)}
                y={
                  height -
                  bottom +
                  18
                }
                textAnchor="middle"
                fontSize={10}
                fill={
                  ETH.textMuted
                }
              >
                {Math.round(
                  value,
                )}
              </text>
            </g>
          ),
        )}

        {/* 拟合起点 */}
        <line
          x1={x(
            fitOriginT,
          )}
          x2={x(
            fitOriginT,
          )}
          y1={top}
          y2={
            height -
            bottom
          }
          stroke="#888"
          strokeDasharray="4 4"
        />

        <text
          x={
            x(
              fitOriginT,
            ) + 5
          }
          y={top + 15}
          fontSize={9}
          fill={
            ETH.textMuted
          }
        >
          拟合起点
        </text>

        {/* 双曲→指数转换点 */}
        {transitionAbsoluteT <=
          forecastEnd && (
          <>
            <line
              x1={x(
                transitionAbsoluteT,
              )}
              x2={x(
                transitionAbsoluteT,
              )}
              y1={top}
              y2={
                height -
                bottom
              }
              stroke="#b64f35"
              strokeDasharray="5 5"
            />

            <text
              x={
                x(
                  transitionAbsoluteT,
                ) + 5
              }
              y={top + 30}
              fontSize={9}
              fill="#b64f35"
            >
              双曲→指数
            </text>
          </>
        )}

        {hyperbolicPath && (
          <path
            d={
              hyperbolicPath
            }
            fill="none"
            stroke="#3d6f91"
            strokeWidth={2.2}
          />
        )}

        {exponentialPath && (
          <path
            d={
              exponentialPath
            }
            fill="none"
            stroke="#b64f35"
            strokeWidth={2.2}
          />
        )}

        {data.map(
          (
            point,
            index,
          ) => {
            const used =
              selectedSet.has(
                `${point.date}|${point.t}`,
              );

            return (
              <circle
                key={`${point.date}-${index}`}
                cx={x(
                  point.t,
                )}
                cy={y(
                  point.q,
                )}
                r={
                  used
                    ? 4
                    : 3
                }
                fill={
                  used
                    ? ETH.text
                    : '#aaa'
                }
              />
            );
          },
        )}

        <text
          x={
            left +
            plotW / 2
          }
          y={height - 8}
          textAnchor="middle"
          fontSize={11}
          fill={
            ETH.textSec
          }
        >
          生产时间 t / d
        </text>

        <text
          x={16}
          y={
            top +
            plotH / 2
          }
          textAnchor="middle"
          fontSize={11}
          fill={
            ETH.textSec
          }
          transform={`rotate(-90 16 ${
            top +
            plotH / 2
          })`}
        >
          日产气量 q / 10⁴m³/d
        </text>
      </svg>
    </div>
  );
}

function StatusCard({
  title,
  value,
  description,
}: {
  title: string;
  value: string;
  description: string;
}) {
  return (
    <div
      style={{
        padding: 14,
        background:
          ETH.canvasBg,
        border: `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color:
            ETH.textMuted,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 18,
          fontWeight: 800,
          fontFamily:
            ETH.fontMono,
        }}
      >
        {value}
      </div>

      <div
        style={{
          marginTop: 4,
          fontSize: 10,
          color:
            ETH.textMuted,
        }}
      >
        {description}
      </div>
    </div>
  );
}

function addDays(
  dateText: string,
  days: number,
) {
  const date =
    new Date(dateText);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return '--';
  }

  date.setDate(
    date.getDate() +
      Math.round(days),
  );

  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() +
        1,
    ).padStart(
      2,
      '0',
    );

  const day =
    String(
      date.getDate(),
    ).padStart(
      2,
      '0',
    );

  return `${year}-${month}-${day}`;
}

function fmt(
  value: number,
  digits = 4,
) {
  if (
    !Number.isFinite(value)
  ) {
    return '--';
  }

  if (
    Math.abs(value) >
      1e6 ||
    (
      Math.abs(value) >
        0 &&
      Math.abs(value) <
        1e-5
    )
  ) {
    return value.toExponential(
      4,
    );
  }

  return value.toFixed(
    digits,
  );
}

const inputStyle:
  React.CSSProperties = {
    minWidth: 160,
    padding: '6px 8px',
    border: `1px solid ${ETH.headerBorder}`,
    background:
      ETH.bg,
    color:
      ETH.text,
    outline:
      'none',
  };

const thStyle:
  React.CSSProperties = {
    padding: '8px',
    borderBottom: `1px solid ${ETH.headerBorder}`,
    textAlign:
      'center',
    color:
      ETH.textSec,
  };

const tdStyle:
  React.CSSProperties = {
    padding: '8px',
    borderBottom: `1px solid ${ETH.headerBorder}`,
  };